import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FileAttachment04, PlusCircle, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../../api';
import { onSuccessCustomApiResponse } from '../../api/utils';
import { formatPrice } from '../../utils/formatPrice';
import { BasicDialog } from '../common/BasicDialog';
import { SourceCommissionDocumentDetail } from './type/sourceCommissions';
import {
  MAX_DOCUMENT_ITEMS,
  buildUpdateBody,
  getEditLimitIssue,
  getErrorCode,
  isValidAmount,
  sumAmounts,
  withCacheBuster
} from './utils';

interface EditRow {
  accidentId: number;
  label: string;
  ownerName: string;
  invoiceNumber: string;
  originalAmount: number;
  amount: string;
  year?: number;
}

const parseAmountInput = (value: string) => Number.parseFloat(value.replace(/\s/g, '').replace(',', '.'));

const toEditRows = (detail: SourceCommissionDocumentDetail): EditRow[] =>
  detail.items.map((i) => ({
    accidentId: i.accidentId,
    label: i.description,
    ownerName: i.ownerName,
    invoiceNumber: i.invoiceNumber,
    originalAmount: i.amount,
    amount: String(i.amount)
  }));

const toPoolRows = (detail: SourceCommissionDocumentDetail): EditRow[] =>
  (detail.available ?? []).map((a) => ({
    accidentId: a.accidentId,
    label: a.contractNumber,
    ownerName: a.ownerName,
    invoiceNumber: a.invoiceNumbers,
    originalAmount: a.amount,
    amount: String(a.amount),
    year: a.year
  }));

interface ExpenseDocumentEditDialogProps {
  documentId?: number;
  onClose: () => void;
}

export const ExpenseDocumentEditDialog = ({ documentId, onClose }: ExpenseDocumentEditDialogProps) => {
  const api = useCommonApi();
  const open = documentId !== undefined;
  const { data, isPending, isError } = useQuery({ ...api.getSourceCommissionDocument(documentId ?? 0), enabled: open });
  const { mutate: update, isPending: isSaving } = useMutation(api.updateSourceCommissionDocument());

  const [items, setItems] = useState<EditRow[]>([]);
  const [pool, setPool] = useState<EditRow[]>([]);
  const [pdf, setPdf] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<number>();

  // Lokální stav se inicializuje jednou při otevření; pozdější refetch ho nesmí přepsat rozeditované řádky.
  useEffect(() => {
    if (!open) {
      setLoadedId(undefined);
      return;
    }
    if (data && data.id === documentId && loadedId !== documentId) {
      setItems(toEditRows(data));
      setPool(toPoolRows(data));
      setPdf(data.pdf);
      setLoadedId(documentId);
    }
  }, [open, data, documentId, loadedId]);

  const parsed = items.map((i) => ({ accidentId: i.accidentId, amount: parseAmountInput(i.amount) }));
  const hasInvalid = parsed.some((i) => !isValidAmount(i.amount));
  const limitIssue = hasInvalid
    ? undefined
    : getEditLimitIssue(parsed, data?.paymentType === 'cash' || data?.paymentMethod === 'cash' ? 'cash' : undefined);
  const canAdd = items.length < MAX_DOCUMENT_ITEMS;
  const canSave = items.length > 0 && !hasInvalid && !limitIssue && !isSaving;

  const removeRow = (row: EditRow) => {
    setItems((prev) => prev.filter((i) => i.accidentId !== row.accidentId));
    setPool((prev) => [...prev, { ...row, amount: String(row.originalAmount) }]);
  };
  const addRow = (row: EditRow) => {
    setPool((prev) => prev.filter((i) => i.accidentId !== row.accidentId));
    setItems((prev) => [...prev, row]);
  };

  const save = () => {
    update(
      { id: documentId as number, items: buildUpdateBody(parsed as { accidentId: number; amount: number }[]).items },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: (_, detail: SourceCommissionDocumentDetail) => {
            setPdf(withCacheBuster(detail.pdf));
            setItems(toEditRows(detail));
          },
          failed: (_, __, { statusCode }) => {
            if (statusCode === 454) onClose();
          }
        }),
        onError: (error) => {
          if (getErrorCode(error) === 454) onClose();
        }
      }
    );
  };

  return (
    <BasicDialog
      open={open}
      onClose={onClose}
      title={data ? `Doklad ${data.documentNumber}` : 'Doklad'}
      fullWidth
      maxWidth="md"
    >
      {isPending && (
        <Stack alignItems="center" p={4}>
          <CircularProgress />
        </Stack>
      )}
      {isError && <Alert severity="error">Doklad se nepodařilo načíst.</Alert>}
      {data && loadedId === documentId && (
        <Stack gap={3}>
          <Typography variant="body2" color="text.secondary">
            Tipař: {data.sourceName}
            {data.sourceTradeName ? ` (${data.sourceTradeName})` : ''}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nehoda</TableCell>
                <TableCell>Majitel</TableCell>
                <TableCell>Faktura</TableCell>
                <TableCell align="right">Částka</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => {
                const invalid = !isValidAmount(parseAmountInput(row.amount));
                return (
                  <TableRow key={row.accidentId}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>{row.ownerName}</TableCell>
                    <TableCell>{row.invoiceNumber}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={row.amount}
                        error={invalid}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((i) => (i.accidentId === row.accidentId ? { ...i, amount: e.target.value } : i))
                          )
                        }
                        slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': 'Částka' } }}
                        sx={{ width: 130 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton aria-label="Odebrat řádek" onClick={() => removeRow(row)}>
                        <Trash01 size="18px" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {items.length === 0 && (
            <Alert severity="warning">
              Doklad nemůže zůstat bez řádků. Pro odstranění celého dokladu použijte smazání v seznamu dokladů.
            </Alert>
          )}

          {limitIssue && <Alert severity="warning">{limitIssue}</Alert>}

          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography>
              Celkem: <b>{formatPrice(sumAmounts(parsed), 2)}</b>
            </Typography>
            <Stack direction="row" gap={2} alignItems="center">
              {pdf && (
                <Link href={pdf} target="_blank" underline="always">
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <FileAttachment04 size="14px" /> Stáhnout PDF
                  </Stack>
                </Link>
              )}
              <Button variant="contained" disabled={!canSave} loading={isSaving} onClick={save}>
                Uložit
              </Button>
            </Stack>
          </Stack>

          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Přidat POJ
            </Typography>
            {!canAdd && (
              <Typography variant="body2" color="text.secondary">
                Doklad už má {MAX_DOCUMENT_ITEMS} POJ, další nelze přidat. Nejdřív některý řádek odeberte.
              </Typography>
            )}
            {pool.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Žádné další nevyplacené POJ tohoto tipaře.
              </Typography>
            ) : (
              pool.map((row) => (
                <Stack key={row.accidentId} direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                  <Typography variant="body2">
                    {row.label} · {row.ownerName} · {formatPrice(row.originalAmount, 2)}
                    {row.year ? ` · rok ${row.year}` : ''}
                  </Typography>
                  <Button
                    size="small"
                    disabled={!canAdd}
                    startIcon={<PlusCircle size="16px" />}
                    onClick={() => addRow(row)}
                  >
                    Přidat
                  </Button>
                </Stack>
              ))
            )}
          </Stack>
        </Stack>
      )}
    </BasicDialog>
  );
};
